import { Box, Flex } from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  useConsecutiveBudgets,
  useConsecutiveExecutions,
} from '@/features/project/projects/detail/activities/_hooks/consecutive';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  AmountUnitLabelModifier,
  BigCalculator,
  formatValueByUnit,
} from '@/utils';
import Highcharts, { type Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { type FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RecurringStatsSelector } from './RecurringStatsSelector';

export const RecurringStatsGraph: FC = memo(() => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { unit } = useLocaledAmount();

  const consecutiveBudgets = useConsecutiveBudgets({ id: projectId });
  const consecutiveExecutions = useConsecutiveExecutions({ id: projectId });

  const graphContents = useMemo(
    () => ({
      plot: {
        fontWeight: 'normal',
      },
      unitText: t('amount-label-with-unit', {
        unit: AmountUnitLabelModifier[unit],
      }),
      tooltip: {
        backgroundColor: '#fff',
        borderColor: 'none',
      },
      series: {
        totalBudgetAmount: {
          name: t('total-budget-amount'),
          color: '#6496FA',
        },
        executionAmount: {
          name: t('execution-amount'),
          color: '#63D9AB',
        },
      },
    }),
    [t, unit],
  );

  const graphOptions = useMemo<Options>(() => {
    const years = Array.from(
      new Set(
        [
          ...(consecutiveBudgets.data ?? []),
          ...(consecutiveExecutions.data ?? []),
        ].map(({ fiscalYear }) => fiscalYear),
      ),
    ).sort((a, b) => a - b);

    const series = [
      {
        type: 'column' as const,
        name: graphContents.series.totalBudgetAmount.name,
        data: years.map((c) => {
          const currentYear =
            consecutiveBudgets.data?.filter(
              ({ fiscalYear }) => fiscalYear === c,
            ) ?? [];

          return currentYear.length
            ? currentYear.reduce(
                (acc, cur) => BigCalculator.add(acc, cur.finalizedAmount ?? 0),
                0,
              )
            : null;
        }),
        color: graphContents.series.totalBudgetAmount.color,
      },
      {
        type: 'column' as const,
        name: graphContents.series.executionAmount.name,
        data: years.map((c) => {
          const currentYear =
            consecutiveExecutions.data?.filter(
              ({ fiscalYear }) => fiscalYear === c,
            ) ?? [];

          return currentYear.length
            ? currentYear.reduce(
                (acc, cur) => BigCalculator.add(acc, cur.amount ?? 0),
                0,
              )
            : null;
        }),
        color: graphContents.series.executionAmount.color,
      },
    ].filter((s) => s.data.some((data) => data !== null));

    return {
      chart: {
        type: 'column',
      },
      legend: {
        enabled: false,
      },
      title: undefined,
      xAxis: {
        categories: years.map((y) => y.toString()),
      },
      yAxis: {
        title: {
          text: graphContents.unitText,
        },
        stackLabels: {
          enabled: true,
        },
        labels: {
          formatter: ({ pos }) =>
            formatValueByUnit(pos, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
        },
      },
      tooltip: {
        shared: true,
        followPointer: true,
        useHTML: true,
        valueSuffix: graphContents.unitText,
        borderColor: graphContents.tooltip.borderColor,
        backgroundColor: graphContents.tooltip.backgroundColor,
        formatter(this) {
          const { x, points } = this;

          const title = `<div style="font-size: 1em;">${x}</div>`;
          const rows = `<div style="display: flex; flex-wrap: wrap; gap: 8px; width: 100%;">${points
            ?.map(
              ({ y, color, series: { name } }) =>
                `<div style="display: flex; gap: 24px; align-items: center; width: 100%; justify-content: space-between;"><div style="display: flex; gap: 8px; align-items: center;"><div style="color:${color}; line-height: 1em;">\u25CF</div>${name}:</div><div style="display: flex; gap: 8px; align-items: center; line-height: 1em;">${formatValueByUnit(
                  y,
                  {
                    operation: 'round',
                    unit,
                    showUnitLabel: true,
                  },
                )}</div></div>`,
            )
            .join('')}</div>`;

          return `<div style="display: flex; flex-wrap: wrap; gap: 8px; width: 100%;">${title}${rows}</div>`;
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.02,
        },
        series: {
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: graphContents.plot.fontWeight,
            },
            formatter() {
              return formatValueByUnit(this.y, {
                operation: 'round',
                unit,
                showUnitLabel: false,
              });
            },
          },
        },
      },
      series,
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }, [
    consecutiveBudgets.data,
    consecutiveExecutions.data,
    unit,
    graphContents,
  ]);

  return (
    <PinpointMarker
      pinpoint={{
        path: { project: projectId },
        field: 'rs.recurring-stats-graph',
      }}
    >
      <Box padding={16} bordered>
        <Flex justify="end">
          <RecurringStatsSelector />
        </Flex>
        <HighchartsReact highcharts={Highcharts} options={graphOptions} />
      </Box>
    </PinpointMarker>
  );
});
